import { element } from 'angular';
import * as THREE from 'three';
import { Matrix4 } from 'three';
// import { arrayify } from "tslint/lib/utils";
import { IndexKind } from 'typescript';
import { result } from 'underscore';
import { object } from '../../../marietta3d/AutoGen';

const FLOAT_SIZE = 4;
const Z_UP = true;
const BBOX_MODE = false;

// const DEBUG_MODE = false;

// if (!DEBUG_MODE) {

//     // let console = console || {};
//     console.log = function(){};
// }

// assert
function assert(actual, expected) {
  console.log('.');
  console.assert(actual === expected, `\nact: ${actual}\nexp: ${expected}`);
}

function toHexString(n) {
  if (n < 0) {
    n = 0xFFFF + n + 1;
  }
  return `0x${(`0000${n.toString(16).toUpperCase()}`).substr(-4)}`;
}

class Node {
  public chunk_id: any;

  public parent: any;

  public childrenArray: any;

  public buffer: any;

  public byteOffset: any;

  public dataView: any;

  /**
   * @param {string} nodeName ノードの名前
   */
  constructor(chunk_id, size) {
    this.chunk_id = chunk_id;
    this.parent = null;
    this.childrenArray = [];
    this.buffer;
    this.byteOffset = 0;
    this.dataView;

    this.createBuffer(size);
  }

  // chunk_id + datasize + bufferのサイズ調整
  // 引数: sizeはバッファを確保するサイズ
  createBuffer(buffer_size) {
    let bsize = buffer_size;
    if (bsize === undefined) {
      bsize = 0;
    }

    const size_ = 6 + bsize;	// SIZE: chunkID(2) + data size(4) + size
    this.buffer = new ArrayBuffer(size_);
    this.dataView = new DataView(this.buffer);

    this.dataView.setUint16(0, this.chunk_id, true);
    this.dataView.setUint32(2, size_, true);
    this.byteOffset = 6;
  }

  appendBuffer(buf) {
    this.buffer = this.mergedBuffer(this.buffer, buf);
    this.dataView = new DataView(this.buffer);
  }

  mergedBuffer(buf1, buf2) {
    const array1 = new Uint8Array(buf1);
    const array2 = new Uint8Array(buf2);

    const mergedArray = new Uint8Array(array1.length + array2.length);
    mergedArray.set(array1);
    mergedArray.set(array2, array1.length);
    return mergedArray.buffer;
  }

  dataset_(buf, pos) {
    const array = new Uint8Array(this.buffer);
    buf.set(array, pos);
    return array.length;
  }

  //
  dataSet(buf, pos) {
    let result = pos;
    const num = this.dataset_(buf, result);
    result += num;

    this.childrenArray.forEach((element) => {
      result = element.dataSet(buf, result);
    });
    return result;
  }

  // ツリー構造Nodeクラスに保持されている各バッファを１つにまとめる
  bufferData() {
    const size = this.getSize();

    const buffer = new ArrayBuffer(size);
    const array = new Uint8Array(buffer);
    this.dataSet(array, 0);
    return array.buffer;
  }

  /* 所属ツリーのルートの取得
  * @return {Node}
  */
  getRoot() {
    if (this.parent === null) return this;
    return this.parent.getRoot();
  }

  /**
  * @param {Node} childNode
  */
  addChild(childNode) {
    if (childNode instanceof Array) {
      childNode.forEach((element) => {
        if (element) {
          this.childrenArray.push(element);
        }
      });
    } else {
      this.childrenArray.push(childNode);
    }
    childNode.parent = this;
  }

  /**
  * 配下のノードから指定したノード名を検索（自分自身も検索対象に含む）
  * @param {array} searchResult 検索結果を格納するための配列
  * @param {string} searchName 検索するノード名
  */
  searchChildren(searchResult, compare) {
    if (compare(this)) searchResult.push(this);
    for (let i = 0; i < this.childrenArray.length; i++) {
      const child = this.childrenArray[i];
      child.searchChildren(searchResult, compare);
    }
  }

  getNode(chunkID) {
    const result = [];
    this.searchChildren(result, (node) => node.chunk_id == chunkID);
    return result;
  }

  buf2hex() {
    return Array.prototype.map.call(new Uint8Array(this.buffer), (x) => (`00${x.toString(16)}`).slice(-2)).join(',');
  }

  /* 階層の取得
  * @return {number}
  */
  getLevel() {
    if (this.parent === null) return 0;
    return this.parent.getLevel() + 1;
  }

  description() {
    const num = this.getLevel();
    const tab = String('  ').repeat(num);
    console.log(`ChunkID: ${tab}${toHexString(this.chunk_id)}\tSize:${this.buffer.byteLength}`);
    for (let i = 0; i < this.childrenArray.length; i++) {
      const child = this.childrenArray[i];
      child.description();
    }
  }

  getSize() {
    let size = 0;
    for (let i = 0; i < this.childrenArray.length; i++) {
      const child = this.childrenArray[i];
      size += child.getSize();
    }
    return size + this.buffer.byteLength;
  }

  calcBufferSize() {
    const size = this.getSize();
    this.dataView.setUint32(2, size, true);

    this.childrenArray.forEach((element) => {
      element.calcBufferSize();
    });
  }
}

/*
  0x4000 : Object block
  ---------------------
    0x4010 : Object hidden
    0x4012 : Object doesn't cast
    0x4013 : Matte object
    0x4015 : External process on
    0x4017 : Object doesn't receive shadows

    0x4100 : Triangular mesh
    0x4110 : Vertices list
    0x4120 : Faces description
      0x4130 : Faces material list
    0x4140 : Mapping coordinates list
      0x4150 : Smoothing group list
    0x4160 : Local coordinate system
    0x4165 : Object color in editor

    0x4181 : External process name
    0x4182 : External process parameters

    0x4600 : Light
    0x4610 : Spotlight
      0x4627 : Spot raytrace
      0x4630 : Light shadowed
      0x4641 : Spot shadow map
      0x4650 : Spot show cone
      0x4651 : Spot is rectangular
      0x4652 : Spot overshoot
      0x4653 : Spot map
      0x4656 : Spot roll
      0x4658 : Spot ray trace bias

    0x4620 : Light off
    0x4625 : Attenuation on
    0x4659 : Range start
    0x465A : Range end
    0x465B : Multiplier

    0x4700 : Camera

  0x7001 : Window settings
    0x7011 : Window description #2 ...
    0x7012 : Window description #1 ...
    0x7020 : Mesh windows ...
*/

// Light, Cameraは未対応

export class TDSExporter {
  parse: any;

  constructor() {
    this.parse = function (scene, options) {
      const TDEditor_chunk = new Node(MDATA, 0);

      let geom_conut = 0;
      let mat_count = 0;
      let mapping_count = 0;
      let count = 0;
      const infoArray = [];

      scene.traverse((object) => {
        if (!object.isMesh) return false;

        const data = {
          object,
          info: {
            objectId: objectId(object),
            colladaId: colladaId(object),
            url: url(object),
            exportName: exportName(object),
            materialName: materialInfo(object),
            export: true,
            bbox: false,
          },
        };
        checkObject(data);

        console.log('object data : ', data);

        if (!data.info.export) return;
        // メッシュ
        const geom_node = createObject(data);				// メッシュデーター作成
        const materials = createMaterial(object.material);	// マテリアル

        TDEditor_chunk.addChild(materials);
        TDEditor_chunk.addChild(geom_node);

        count++;
      });

      // TDEditor_chunk.childrenArray.sort(function(a, b){
      // 	const id1 = a.chunk_id;
      // 	const id2 = b.chunk_id;
      // 	if( id1 < id2 ) return -1;
      // 	if( id1 > id2 ) return 1;
      // 	return 0;
      // })

      const root = new Node(M3DMAGIC, 0); // root node

      root.addChild(TDEditor_chunk);
      root.calcBufferSize();
      // console.log(root.buf2hex());
      console.log('Total : ', root.getSize());

      const buffer = root.bufferData();

      console.log('Buffer Size : ', buffer.byteLength);

      return buffer;

      // オブジェクトの出力対象チェックを行う
      // 真なら出力
      function checkObject(data) {
        const exportObj = function (data) {
          const { object } = data;

          const obj_type = dataType(object);

          // 空や地面などの形状、寸法表示用形状はスキップ
          if (obj_type == 'Environment' || obj_type == 'Label') {
            return false;
          }

          // 非表示形状はスキップ
          if (!checkVisible(object)) return false;
          if (!object.geometry.isBufferGeometry) return false;
          if (data.info.colladaId && data.info.colladaId.indexOf('site') >= 0) return false;
          return true;
        };

        const checkBBox = function (data) {
          // 3DSの仕様により16bit(65536)までしか頂点の数が扱えないのでその場合BBOX形状へ変換する
          if (data.object.geometry.getAttribute('position').count > 65535) return true;
          if (!BBOX_MODE) return false;

          if (data.info.objectId) {
            if (data.info.objectId.indexOf('fur') >= 0) return true;
            if (data.info.objectId.indexOf('equ') >= 0) return true;
            if (data.info.objectId.indexOf('ext') >= 0) return true;
          }
          return false;
        };

        if (!exportObj(data)) {
          data.info.export = false;
          return;
        }
        data.info.bbox = checkBBox(data);
      }

      function exportName(object): String {
        return `obj.${geom_conut}`;
      }
      function materialInfo(object): Array<String> {
        const result = [];
        let count = mapping_count;

        const mappinginfo = function (material) {
          const result = [];
          [
            material.map,
            material.bumpMap,
            material.alphaMap,
            material.specularMap,
          ].forEach((map) => {
            if (!map) return;
            const info = {
              url: map.url,
              name: map.name,
              mapName: `map${count}.jpg`,
            };
            result.push(info);
            count++;
          });
          return result;
        };

        object.geometry.groups.forEach((group) => {
          const info = { name: '', mapinfo: null };
          const num = mat_count + group.materialIndex;
          const name = `mat.${num}`;
          info.name = name;
          info.mapinfo = mappinginfo(object.material);

          result.push(info);
        });
        return result;
      }

      // objectを親まで辿りfunc関数で一致したらobjectを返す
      function findObject(object, func) {
        let obj = object;

        while (obj) {
          if (func(obj)) return obj;
          obj = obj.parent;
        }
        return null;
      }
      function getProperty(object, func) {
        let obj = object;

        while (obj) {
          const result = func(obj);
          if (result) return result;
          obj = obj.parent;
        }
        return null;
      }

      function dataType(object): string {
        const obj = findObject(object, (object) => object.userData.type);
        if (obj) return obj.userData.type;
        return '';
      }
      // 形状を親まで辿り途中非表示ならfalse,親まで表示ならtrueを返す
      function checkVisible(object): boolean {
        const obj = findObject(object, (object) => object.visible == false);
        return obj == null;
      }
      function objectId(object): string {
        const obj = findObject(object, (object) => object.objectId);
        if (obj) return obj.objectId;
        return '';
      }
      function colladaId(object): string {
        return getProperty(object, (object) => object.colladaId);
      }
      function url(object): string {
        return getProperty(object, (object) => object.url);
      }

      // 0x4110 : Vertices list
      // 頂点座標の登録
      // 頂点数 + 各頂点 X,Y,Z座標
      // このチャンクのサブチャンクは無い
      function Vertices(object): Node {
        const vertices = object.geometry.getAttribute('position');
        const num = vertices.count;

        console.log('Number of Vertices: ', num);

        const node = new Node(POINT_ARRAY, 2 + num * FLOAT_SIZE * 3);

        writeWord(node, num);

        let x_rot = new THREE.Matrix4();
        x_rot.identity();
        if (Z_UP) {
          x_rot = new Matrix4().makeRotationX(Math.PI / 2);	// X軸で９０度回転
        }
        const mat = new THREE.Matrix4().multiplyMatrices(x_rot, object.matrixWorld);
        const start = 0;
        const end = vertices.array.length;
        const vec = new THREE.Vector3();
        for (let i = start; i < end; i += 3) {
          // let vertex = new THREE.Vector3(vertices.array[i], vertices.array[i+1], vertices.array[i+2]);
          vec.x = vertices.array[i];
          vec.y = vertices.array[i + 1];
          vec.z = vertices.array[i + 2];
          vec.applyMatrix4(mat);
          writePosition(node, vec.x, vec.y, vec.z);
        }

        node.description();
        return node;
      }

      //
      // 0x4120 : Faces description
      // 面の数 + (面を構成する頂点のインデックス番号 * 3頂点 + フラグ) * 面数
      function Faces(geometry): Node {
        const hasIndex = function (geometry) {
          return geometry.index;
        };

        const getPosCount = function (geometry) {
          return geometry.getAttribute('position').count;
        };
        const createNode = function (num) {
          const node = new Node(FACE_ARRAY, 2 + (2 + 2 + 2 + 2) * num);

          console.log('Number of Faces: ', num);

          writeWord(node, num);
          return node;
        };
        let node = null;

        if (hasIndex(geometry)) {
          const indices = geometry.index.array;
          const num = indices.length / 3;

          node = createNode(num);

          for (let i = 0; i < indices.length; i += 3) {
            writeFaceIndexes(node, indices[i], indices[i + 1], indices[i + 2], 0);
          }
        } else {
          // geometryに面のインデックス配列がないケース
          const n = getPosCount(geometry);
          const num = n / 3;

          node = createNode(num);

          for (let i = 0; i < n; i += 3) {
            writeFaceIndexes(node, i, i + 1, i + 2, 0);
          }
        }

        // console.log(node.buf2hex());
        node.description();
        return node;
      }
      // Chunk #    : 0x4140
      // Name       : Mapping coordinates list for each vertex
      //
      // Three.jsのコードを見るとUVは各頂点に対して２つまでセット出来るが
      // １つ目のUV値だけ対応
      function UVs(geometry): Node {
        // const uvs = geometry.faceVertexUvs[0];
        const uvAttribute = geometry.getAttribute('uv');
        if (!uvAttribute) return null;

        const num = uvAttribute.count;

        console.log('Number of UVs: ', num);

        const node = new Node(TEX_VERTS, 2 + num * FLOAT_SIZE * 2);
        writeWord(node, num);
        const start = 0;
        const end = uvAttribute.array.length;
        for (let i = start; i < end; i += 2) {
          writeFloat2(node, uvAttribute.array[i], uvAttribute.array[i + 1]);
        }
        return node;
      }
      //
      // 0x4130 : Faces material list
      // 0x4130は0x4120: Faces descriptionのサブチャンク
      //
      function createFacesMaterialList(object) {
        const faceCount = function (geometry) {
          if (geometry.index) return geometry.index.count / 3;

          return geometry.getAttribute('position').count / 3;
        };
        // マテリアル番号
        // count 大きさ
        // start スタート
        const makeBuffer = function (number, count, start) {
          const node = new Node(MSH_MAT_GROUP, 0);
          const buf = strToUFT8(`mat.${number}`);
          node.appendBuffer(buf);

          const buffer1 = new Uint16Array(1);
          buffer1[0] = count;
          // let buffer2  = new Uint16Array(count).map((_, i) => start + i);
          const buffer2 = Uint16Array.from({ length: count }, (_, i) => start + i);

          // buffer1とbuffer2をマージする
          const mergedArray = new Uint16Array(buffer1.length + buffer2.length);
          mergedArray.set(buffer1);
          mergedArray.set(buffer2, buffer1.length);

          node.appendBuffer(mergedArray.buffer);
          return node;
        };

        const { groups } = object.geometry;
        const result = [];

        if (!groups) {
          const node = makeBuffer(mat_count, faceCount(object.geometry), 0);
          result.push(node);
        } else {
          groups.forEach((group) => {
            const node = makeBuffer(mat_count + group.materialIndex, group.count, group.start);
            result.push(node);
          });
        }
        return result;
      }
      //
      // 0x4100 : Triangular mesh
      // メッシュデーターの起点となるチャンク
      //
      function Mesh(object): Node {
        const geometoryData = [
          Vertices(object),
          Faces(object.geometry),
          UVs(object.geometry),
        ];
        const node = new Node(N_TRI_OBJECT, 0);
        node.addChild(geometoryData);

        const mlist = createFacesMaterialList(object);

        const node_ = node.getNode(FACE_ARRAY);
        if (node_ && mlist) {
          node_[0].addChild(mlist);
          node_[0].description();
        }

        return node;
      }
      // 0x4000 : Object block
      // オブジェクトの起点となるチャンク
      // オブジェクト名称（文字列+終端0 + sub Chunk）
      function createObject(data): Node {
        function getCenterPoint(mesh) {
          const middle = new THREE.Vector3();
          const { geometry } = mesh;

          geometry.computeBoundingBox();

          middle.x = (geometry.boundingBox.max.x + geometry.boundingBox.min.x) / 2;
          middle.y = (geometry.boundingBox.max.y + geometry.boundingBox.min.y) / 2;
          middle.z = (geometry.boundingBox.max.z + geometry.boundingBox.min.z) / 2;

          // mesh.localToWorld( middle );
          return middle;
        }
        // 渡された形状をBBOX形状へと変換
        const convertBBOX = function (object) {
          let obj = object;
          const tmpBox = new THREE.Box3().setFromObject(obj);

          const center = getCenterPoint(obj);

          const dimensions = new THREE.Vector3().subVectors(tmpBox.max, tmpBox.min);
          const boxGeo = new THREE.BoxGeometry(dimensions.x, dimensions.y, dimensions.z);

          obj = new THREE.Mesh(boxGeo, new THREE.MeshBasicMaterial());

          const position = obj.geometry.attributes.position.array;

          for (let i = 0; i < position.length; i += 3) {
            position[i] += center.x;
            position[i + 1] += center.y;
            position[i + 2] += center.z;
          }

          obj.matrixWorld = object.matrixWorld;
          return obj;
        };

        let obj = data.object;

        if (data.info.bbox) {
          obj = convertBBOX(data.object);
        }

        const node = new Node(NAMED_OBJECT, 0);
        const buf = strToUFT8(`obj.${geom_conut}`);
        node.appendBuffer(buf);

        const mesh = Mesh(obj);
        if (mesh) {
          node.addChild(mesh);
        }

        node.description();
        geom_conut++;
        return node;
      }

      //----------------------------------------------------------------------------
      // 		Material
      //----------------------------------------------------------------------------
      function createMaterial(material) {
        if (!material) return null;
        const materials = Array.isArray(material) ? material : [material];
        const result = [];
        materials.forEach((element) => {
          const node = MaterialBlock(element);
          // TDEditor_chunk.addChild(node);
          result.push(node);
        });
        return result;
      }

      // - マテリアルの名称 -
      function MaterialName(material): Node {
        const node = new Node(MAT_NAME, 0);
        node.appendBuffer(strToUFT8(`mat.${mat_count}`));
        mat_count++;
        return node;
      }

      // テクスチャマッピング関連処理
      function MapParam(chunk_id, value): Node {
        const node = new Node(chunk_id, 4);
        writeFloatValue(node, value);
        return node;
      }
      function MapUScale(map): Node {
        return MapParam(MAT_MAP_USCALE, map.repeat.x);
      }
      function MapVScale(map): Node {
        return MapParam(MAT_MAP_VSCALE, map.repeat.y);
      }
      function MapUOffset(map): Node {
        return MapParam(MAT_MAP_UOFFSET, map.offset.x);
      }
      function MapVOffset(map): Node {
        return MapParam(MAT_MAP_VOFFSET, map.offset.y);
      }
      function MappingFilename(map): Node {
        console.log('map url: ', map.url);
        console.log('map name:', map.name);

        // map.url.split('/').pop();

        const node = new Node(MAT_MAPNAME, 0);
        const map_name = `map${mapping_count}.jpg`;
        const buf = strToUFT8(map_name);
        node.appendBuffer(buf);

        mapping_count++;

        return node;
      }
      function MapParams(map) {
        return [
          MappingFilename(map),
          MapUScale(map),
          MapVScale(map),
          MapUOffset(map),
          MapVOffset(map),
        ];
      }
      function TextureMap(chunk_id, map): Node {
        if (!map) return null;
        const node = new Node(chunk_id, 0);
        node.addChild(MapParams(map));
        return node;
      }
      function TextureMaps(material) {
        return [
          TextureMap(MAT_TEXMAP, material.map),
          TextureMap(MAT_BUMPMAP, material.bumpMap),
          TextureMap(MAT_OPACMAP, material.alphaMap),
          TextureMap(MAT_SPECMAP, material.specularMap),
        ];
      }

      // -----------------------------------------------------------------------------
      // Chunk #    : 0x0010
      // Name       : Rgb color (float format)
      // Level      : global chunk
      // Size       : 12
      // Format     :
      //
      //   float    red
      //   float    green
      //   float    blue
      //
      // This chunk contains 3 floats : the red, green and blue components of a
      // color. The values range from 0.0 to 1.0 (full intensity).
      function Color(chunid, color): Node {
        if (!color) return null;
        const node = new Node(COLOR_F, FLOAT_SIZE * 3);		// float(R,G,B) * 3
        writeColor(node, color.r, color.g, color.b);

        const result = new Node(chunid, 0);
        result.addChild(node);
        return result;
      }
      function wireSize(material): Node {
        const node = new Node(MAT_WIRE_SIZE, 1);
        writeByte(node, material.wireframeLinewidth);
        return node;
      }

      function AmbientColor(material): Node {
        return Color(MAT_AMBIENT, material.color);
      }
      function Diffuse(material): Node {
        return Color(MAT_DIFFUSE, material.color);
      }
      function Specular(material) {
        return Color(MAT_SPECULAR, material.specular);
      }
      function IntPercentage(chunid, val) {
        const node = new Node(INT_PERCENTAGE, 2);

        writeWord(node, val);

        const result = new Node(chunid, 0);
        result.addChild(node);
        return result;
      }
      function Shininess(material) {
        if (!material.shininess) return null;
        return IntPercentage(MAT_SHININESS, material.shininess);
      }

      // opacityは不透明度　0で透明、１で不透明
      // transparencyは透明度　0で不透明、１で透明
      // ここではtransparencyの整数値をセット
      function Transparency(material) {
        if (!material.transparent) return null;
        return IntPercentage(MAT_TRANSPARENCY, 100 - material.opacity * 100);
      }
      function materialParam(material) {
        return [
          MaterialName(material),
          wireSize(material),
          AmbientColor(material),
          Diffuse(material),
          Specular(material),
          Shininess(material),
          Transparency(material),
        ];
      }
      function MaterialBlock(material): Node {
        const node = new Node(MAT_ENTRY, 0);
        node.addChild(materialParam(material));
        node.addChild(TextureMaps(material));
        // materialParam.forEach(element => {
        // 	if (element) {
        // 		node.addChild(element);
        // 	}
        // });

        return node;
      }

      root.description();
      console.log(root.getSize());

      function writeFloatValue(node, val) {
        node.dataView.setFloat32(node.byteOffset, val, true);
        node.byteOffset += 4;
      }
      function writeUInt16Value(node, val) {
        node.dataView.setUint16(node.byteOffset, val, true);
        node.byteOffset += 2;
      }

      /**
       * Write 32 bit float value.
       *
       * @method writePosition
       * @param {DataView} node Dataview to write data from.
       */
      function writeFloat3(node, x, y, z) {
        writeFloatValue(node, x);
        writeFloatValue(node, y);
        writeFloatValue(node, z);
      }
      function writeFloat2(node, x, y) {
        writeFloatValue(node, x);
        writeFloatValue(node, y);
      }
      function writePosition(node, x, y, z) {
        writeFloat3(node, x, y, z);
      }
      function writeColor(node, r, g, b) {
        writeFloat3(node, r, g, b);
      }
      function writeFaceIndexes(node, a, b, c, flag) {
        writeUInt16Value(node, a);
        writeUInt16Value(node, b);
        writeUInt16Value(node, c);
        writeUInt16Value(node, flag);
      }
      function writeWord(node, val) {
        // node.dataView.setUint16(node.byteOffset, val, true);
        // node.byteOffset += 2;
        writeUInt16Value(node, val);
      }
      function writeByte(data, val) {
        data.dataView.setUint8(data.byteOffset, val, true);
        data.byteOffset++;
      }

      // 文字列から ArrayBuffer への変換
      function string_to_buffer(src) {
        return (new Uint16Array([].map.call(src, (c) => c.charCodeAt(0)))).buffer;
      }

      // string(utf16)からutf8へ変換
      // 3DSの仕様で文字列の終端は0
      function strToUFT8(src) {
        const encoder = new TextEncoder();
        const view = encoder.encode(src);

        const array = new Uint8Array(view.length + 1);
        array.set(view);
        return array.buffer;
      }
    };
  }
}

const NULL_CHUNK = 0x0000;
var M3DMAGIC = 0x4D4D;
// var SMAGIC = 0x2D2D;
// var LMAGIC = 0x2D3D;
const MLIBMAGIC = 0x3DAA;
// var MATMAGIC = 0x3DFF;
const CMAGIC = 0xC23D;
const M3D_VERSION = 0x0002;
// var M3D_KFVERSION = 0x0005;
var COLOR_F = 0x0010;
const COLOR_24 = 0x0011;
const LIN_COLOR_24 = 0x0012;
const LIN_COLOR_F = 0x0013;
var INT_PERCENTAGE = 0x0030;
// var FLOAT_PERCENTAGE = 0x0031;
var MDATA = 0x3D3D;
const MESH_VERSION = 0x3D3E;
const MASTER_SCALE = 0x0100;
// var LO_SHADOW_BIAS = 0x1400;
// var HI_SHADOW_BIAS = 0x1410;
// var SHADOW_MAP_SIZE = 0x1420;
// var SHADOW_SAMPLES = 0x1430;
// var SHADOW_RANGE = 0x1440;
// var SHADOW_FILTER = 0x1450;
// var RAY_BIAS = 0x1460;
// var O_CONSTS = 0x1500;
// var AMBIENT_LIGHT = 0x2100;
// var BIT_MAP = 0x1100;
// var SOLID_BGND = 0x1200;
// var V_GRADIENT = 0x1300;
// var USE_BIT_MAP = 0x1101;
// var USE_SOLID_BGND = 0x1201;
// var USE_V_GRADIENT = 0x1301;
// var FOG = 0x2200;
// var FOG_BGND = 0x2210;
// var LAYER_FOG = 0x2302;
// var DISTANCE_CUE = 0x2300;
// var DCUE_BGND = 0x2310;
// var USE_FOG = 0x2201;
// var USE_LAYER_FOG = 0x2303;
// var USE_DISTANCE_CUE = 0x2301;
var MAT_ENTRY = 0xAFFF;
var MAT_NAME = 0xA000;
var MAT_AMBIENT = 0xA010;
var MAT_DIFFUSE = 0xA020;
var MAT_SPECULAR = 0xA030;
var MAT_SHININESS = 0xA040;
// var MAT_SHIN2PCT = 0xA041;
var MAT_TRANSPARENCY = 0xA050;
// var MAT_XPFALL = 0xA052;
// var MAT_USE_XPFALL = 0xA240;
// var MAT_REFBLUR = 0xA053;
// var MAT_SHADING = 0xA100;
// var MAT_USE_REFBLUR = 0xA250;
// var MAT_SELF_ILLUM = 0xA084;
const MAT_TWO_SIDE = 0xA081;
// var MAT_DECAL = 0xA082;
const MAT_ADDITIVE = 0xA083;
const MAT_WIRE = 0xA085;
// var MAT_FACEMAP = 0xA088;
// var MAT_TRANSFALLOFF_IN = 0xA08A;
// var MAT_PHONGSOFT = 0xA08C;
// var MAT_WIREABS = 0xA08E;
var MAT_WIRE_SIZE = 0xA087;
var MAT_TEXMAP = 0xA200;
// var MAT_SXP_TEXT_DATA = 0xA320;
// var MAT_TEXMASK = 0xA33E;
// var MAT_SXP_TEXTMASK_DATA = 0xA32A;
// var MAT_TEX2MAP = 0xA33A;
// var MAT_SXP_TEXT2_DATA = 0xA321;
// var MAT_TEX2MASK = 0xA340;
// var MAT_SXP_TEXT2MASK_DATA = 0xA32C;
var MAT_OPACMAP = 0xA210;
// var MAT_SXP_OPAC_DATA = 0xA322;
// var MAT_OPACMASK = 0xA342;
// var MAT_SXP_OPACMASK_DATA = 0xA32E;
var MAT_BUMPMAP = 0xA230;
// var MAT_SXP_BUMP_DATA = 0xA324;
// var MAT_BUMPMASK = 0xA344;
// var MAT_SXP_BUMPMASK_DATA = 0xA330;
var MAT_SPECMAP = 0xA204;
// var MAT_SXP_SPEC_DATA = 0xA325;
// var MAT_SPECMASK = 0xA348;
// var MAT_SXP_SPECMASK_DATA = 0xA332;
// var MAT_SHINMAP = 0xA33C;
// var MAT_SXP_SHIN_DATA = 0xA326;
// var MAT_SHINMASK = 0xA346;
// var MAT_SXP_SHINMASK_DATA = 0xA334;
// var MAT_SELFIMAP = 0xA33D;
// var MAT_SXP_SELFI_DATA = 0xA328;
// var MAT_SELFIMASK = 0xA34A;
// var MAT_SXP_SELFIMASK_DATA = 0xA336;
// var MAT_REFLMAP = 0xA220;
// var MAT_REFLMASK = 0xA34C;
// var MAT_SXP_REFLMASK_DATA = 0xA338;
// var MAT_ACUBIC = 0xA310;
var MAT_MAPNAME = 0xA300;
// var MAT_MAP_TILING = 0xA351;
// var MAT_MAP_TEXBLUR = 0xA353;
var MAT_MAP_USCALE = 0xA354;
var MAT_MAP_VSCALE = 0xA356;
var MAT_MAP_UOFFSET = 0xA358;
var MAT_MAP_VOFFSET = 0xA35A;
// var MAT_MAP_ANG = 0xA35C;
// var MAT_MAP_COL1 = 0xA360;
// var MAT_MAP_COL2 = 0xA362;
// var MAT_MAP_RCOL = 0xA364;
// var MAT_MAP_GCOL = 0xA366;
// var MAT_MAP_BCOL = 0xA368;
var NAMED_OBJECT = 0x4000;
// var N_DIRECT_LIGHT = 0x4600;
// var DL_OFF = 0x4620;
// var DL_OUTER_RANGE = 0x465A;
// var DL_INNER_RANGE = 0x4659;
// var DL_MULTIPLIER = 0x465B;
// var DL_EXCLUDE = 0x4654;
// var DL_ATTENUATE = 0x4625;
// var DL_SPOTLIGHT = 0x4610;
// var DL_SPOT_ROLL = 0x4656;
// var DL_SHADOWED = 0x4630;
// var DL_LOCAL_SHADOW2 = 0x4641;
// var DL_SEE_CONE = 0x4650;
// var DL_SPOT_RECTANGULAR = 0x4651;
// var DL_SPOT_ASPECT = 0x4657;
// var DL_SPOT_PROJECTOR = 0x4653;
// var DL_SPOT_OVERSHOOT = 0x4652;
// var DL_RAY_BIAS = 0x4658;
// var DL_RAYSHAD = 0x4627;
// var N_CAMERA = 0x4700;
// var CAM_SEE_CONE = 0x4710;
// var CAM_RANGES = 0x4720;
// var OBJ_HIDDEN = 0x4010;
// var OBJ_VIS_LOFTER = 0x4011;
// var OBJ_DOESNT_CAST = 0x4012;
// var OBJ_DONT_RECVSHADOW = 0x4017;
// var OBJ_MATTE = 0x4013;
// var OBJ_FAST = 0x4014;
// var OBJ_PROCEDURAL = 0x4015;
// var OBJ_FROZEN = 0x4016;
var N_TRI_OBJECT = 0x4100;
var POINT_ARRAY = 0x4110;
// var POINT_FLAG_ARRAY = 0x4111;
var FACE_ARRAY = 0x4120;
var MSH_MAT_GROUP = 0x4130;
// var SMOOTH_GROUP = 0x4150;
// var MSH_BOXMAP = 0x4190;
var TEX_VERTS = 0x4140;
const MESH_MATRIX = 0x4160;
// var MESH_COLOR = 0x4165;
// var MESH_TEXTURE_INFO = 0x4170;
// var KFDATA = 0xB000;
// var KFHDR = 0xB00A;
// var KFSEG = 0xB008;
// var KFCURTIME = 0xB009;
// var AMBIENT_NODE_TAG = 0xB001;
// var OBJECT_NODE_TAG = 0xB002;
// var CAMERA_NODE_TAG = 0xB003;
// var TARGET_NODE_TAG = 0xB004;
// var LIGHT_NODE_TAG = 0xB005;
// var L_TARGET_NODE_TAG = 0xB006;
// var SPOTLIGHT_NODE_TAG = 0xB007;
// var NODE_ID = 0xB030;
// var NODE_HDR = 0xB010;
// var PIVOT = 0xB013;
// var INSTANCE_NAME = 0xB011;
// var MORPH_SMOOTH = 0xB015;
// var BOUNDBOX = 0xB014;
// var POS_TRACK_TAG = 0xB020;
// var COL_TRACK_TAG = 0xB025;
// var ROT_TRACK_TAG = 0xB021;
// var SCL_TRACK_TAG = 0xB022;
// var MORPH_TRACK_TAG = 0xB026;
// var FOV_TRACK_TAG = 0xB023;
// var ROLL_TRACK_TAG = 0xB024;
// var HOT_TRACK_TAG = 0xB027;
// var FALL_TRACK_TAG = 0xB028;
// var HIDE_TRACK_TAG = 0xB029;
// var POLY_2D = 0x5000;
// var SHAPE_OK = 0x5010;
// var SHAPE_NOT_OK = 0x5011;
// var SHAPE_HOOK = 0x5020;
// var PATH_3D = 0x6000;
// var PATH_MATRIX = 0x6005;
// var SHAPE_2D = 0x6010;
// var M_SCALE = 0x6020;
// var M_TWIST = 0x6030;
// var M_TEETER = 0x6040;
// var M_FIT = 0x6050;
// var M_BEVEL = 0x6060;
// var XZ_CURVE = 0x6070;
// var YZ_CURVE = 0x6080;
// var INTERPCT = 0x6090;
// var DEFORM_LIMIT = 0x60A0;
// var USE_CONTOUR = 0x6100;
// var USE_TWEEN = 0x6110;
// var USE_SCALE = 0x6120;
// var USE_TWIST = 0x6130;
// var USE_TEETER = 0x6140;
// var USE_FIT = 0x6150;
// var USE_BEVEL = 0x6160;
// var DEFAULT_VIEW = 0x3000;
// var VIEW_TOP = 0x3010;
// var VIEW_BOTTOM = 0x3020;
// var VIEW_LEFT = 0x3030;
// var VIEW_RIGHT = 0x3040;
// var VIEW_FRONT = 0x3050;
// var VIEW_BACK = 0x3060;
// var VIEW_USER = 0x3070;
// var VIEW_CAMERA = 0x3080;
// var VIEW_WINDOW = 0x3090;
// var VIEWPORT_LAYOUT_OLD = 0x7000;
// var VIEWPORT_DATA_OLD = 0x7010;
// var VIEWPORT_LAYOUT = 0x7001;
// var VIEWPORT_DATA = 0x7011;
// var VIEWPORT_DATA_3 = 0x7012;
// var VIEWPORT_SIZE = 0x7020;
// var NETWORK_VIEW = 0x7030;
